import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PaymentsOutlined } from '@mui/icons-material';
import StyledButton from '../shared/Button/StyledButton';
import { useTranslation } from '../../hooks/useTranslation';
import { useSnackBar } from '../../hooks/useSnackBar';
import { updateCompany } from '../../services/companyService';
import Company from '../../interfaces/Company';
import {
  formatOverageEurForInput,
  overageCreditsFromEur,
} from '../../helpers/overageLimitDisplay';
import { isPaidMeteredPlan } from '../../helpers/creditUsageDisplay';
import type { UsageResponse } from '../../services/billingService';

interface BillingOverageLimitCardProps {
  company: Company | null;
  companyId: string;
  usage: UsageResponse | null;
  onSaved: () => Promise<void> | void;
}

const BillingOverageLimitCard: React.FC<BillingOverageLimitCardProps> = ({
  company,
  companyId,
  usage,
  onSaved,
}) => {
  const { t } = useTranslation();
  const showSnackBar = useSnackBar();
  const paidMetered = usage
    ? isPaidMeteredPlan(usage)
    : ['starter', 'business'].includes(
        (company?.plan_tier ?? '').toLowerCase()
      );

  // Prefer get_usage (reads company doc) then company context — stored as credits.
  const storedCredits =
    usage?.overage_credits_limit ?? company?.overage_credits_limit ?? null;

  const initialDisplay = useMemo(
    () => formatOverageEurForInput(storedCredits),
    [storedCredits]
  );

  const [value, setValue] = useState(initialDisplay);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initialDisplay);
  }, [initialDisplay]);

  const creditEquivalent = useMemo(() => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return Math.round(overageCreditsFromEur(parsed) * 100) / 100;
  }, [value]);

  if (!paidMetered) {
    return null;
  }

  const handleSave = async () => {
    const trimmed = value.trim();
    let overageCreditsLimit: number | null;
    if (trimmed === '') {
      overageCreditsLimit = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        showSnackBar(t('Enter a valid non-negative number'), 'error');
        return;
      }
      const euros = Math.round(parsed * 100) / 100;
      overageCreditsLimit =
        Math.round(overageCreditsFromEur(euros) * 100) / 100;
    }

    setSaving(true);
    try {
      await updateCompany(companyId, {
        overage_credits_limit: overageCreditsLimit,
      });
      await onSaved();
      showSnackBar(t('Overage limit saved'), 'success');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('Error');
      showSnackBar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const helperText =
    creditEquivalent == null
      ? t('Overage limit helper euros')
      : t('Overage limit helper euros with credits', {
          credits: creditEquivalent,
        });

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        bgcolor: '#FFFFFF',
        boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: { md: 260 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(500px 190px at 0% 0%, rgba(15, 118, 110, 0.12), transparent 66%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(135deg, rgba(15, 118, 110, 0.95), rgba(13, 148, 136, 0.9))',
            boxShadow: '0 6px 14px rgba(15, 118, 110, 0.28)',
          }}
        >
          <PaymentsOutlined sx={{ fontSize: '1.05rem', color: '#FFFFFF' }} />
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {t('Pay as you go overage limit')}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        sx={{
          position: 'relative',
          mt: 0.25,
          fontWeight: 700,
          letterSpacing: '-0.015em',
          color: '#0F172A',
        }}
      >
        {t('Max overage (euros)')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ position: 'relative', mt: 0.6, mb: 2, lineHeight: 1.6, flex: 1 }}
      >
        {t('Overage limit intro')}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'flex-start' }}
        sx={{ position: 'relative', mt: 'auto' }}
      >
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={e => setValue(e.target.value)}
          helperText={helperText}
          inputProps={{
            min: 0,
            step: 0.01,
            'aria-label': t('Max overage (euros)'),
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">€</InputAdornment>,
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              height: 40,
            },
          }}
        />
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          sx={{
            height: 40,
            minHeight: 40,
            px: 2.5,
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          {t('Save')}
        </StyledButton>
      </Stack>
    </Box>
  );
};

export default BillingOverageLimitCard;
